import React, { useEffect, useRef, useState } from "react";
import SelectSearchOption from "./SelectSearchOption";
import AppIcons from "../../../utils/AppIcons";

interface SelectSearchProps {
  id: string;
  label: string;
  options: SelectSearchOption[];
  value: number | string | undefined;
  noValueMessage: string;
  onSearch: (inputID: string, keyword: string) => void;
  onChange: (inputID: string, newValue: number | string | undefined) => void;
  topCSS?: string | undefined;
  clearFilterLabel?: string;
  showClearFilter?: boolean;
}

function SelectWithSearch({
  id,
  label,
  options,
  value,
  noValueMessage,
  onChange,
  onSearch,
  topCSS = undefined,
  clearFilterLabel = "clear filter",
  showClearFilter = true,
}: SelectSearchProps) {
  // indicates if the search input field is visible
  const [editIsOn, setEditIsOn] = useState(false);
  // keeps keyword information
  const [keyword, setKeyword] = useState("");
  // reference to the search hierarchy
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (event: MouseEvent | TouchEvent) => {
      if (
        editIsOn &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setEditIsOn(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [editIsOn]);

  const onKeywordChange = (value: string) => {
    setKeyword(value);
    onSearch(id, value);
  };

  const onSelectValue = (value: number | string | undefined) => {
    onChange(id, value);
    setEditIsOn(false);
  };

  const selectedOption = options.filter((o) => o.value === value);
  const display =
    selectedOption && selectedOption.length > 0
      ? selectedOption[0].label
      : noValueMessage;

  // inject in the options the possibility to clear the filter
  const optionsDisplay: SelectSearchOption[] = JSON.parse(
    JSON.stringify(options)
  );
  if (showClearFilter) {
    optionsDisplay.push(
      new SelectSearchOption(clearFilterLabel, undefined, "select-search-clear")
    );
  }

  return (
    <div ref={containerRef} className={topCSS ?? "select-search"}>
      <div className="select-search-label">{label}</div>
      <div>
        {editIsOn && (
          <input
            type="text"
            value={keyword}
            autoFocus={true}
            className="select-search-input"
            onChange={(e) => {
              onKeywordChange(e.target.value);
            }}
          />
        )}
        {!editIsOn && (
          <div
            onClick={() => setEditIsOn(true)}
            className="select-search-display text-left"
          >
            {display}
            {AppIcons.CHEVRON_DOWN}
          </div>
        )}
      </div>
      {editIsOn && (
        <div>
          <div>
            {optionsDisplay.map((option) => {
              return (
                <div
                  onClick={() => onSelectValue(option.value)}
                  className={"select-search-option text-left " + option.css}
                >
                  {option.label}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default SelectWithSearch;
